import { useState, useEffect, useCallback } from "react";
import { Modal } from "@/components/ui/modal";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface MediaCarouselModalProps {
  isOpen: boolean;
  onClose: () => void;
  media?: string[];
  initialIndex?: number;
}

export const MediaCarouselModal: React.FC<MediaCarouselModalProps> = ({
  isOpen,
  onClose,
  media = [],
  initialIndex = 0,
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex, isOpen]);

  const prev = useCallback(() => {
    setCurrentIndex((i) => (i - 1 + media.length) % media.length);
  }, [media.length]);

  const next = useCallback(() => {
    setCurrentIndex((i) => (i + 1) % media.length);
  }, [media.length]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, prev, next]);

  if (!media.length) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="" showHeader={false} size="xl">
      <div className="relative flex items-center justify-center p-4 select-none">
        {/* Prev */}
        {media.length > 1 && (
          <button
            onClick={prev}
            className="absolute left-2 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
            aria-label="Previous"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        )}

        {/* Image */}
        <img
          key={currentIndex}
          src={media[currentIndex]}
          alt={`Media ${currentIndex + 1}`}
          className="max-w-full max-h-[75vh] object-contain rounded-xl"
        />

        {/* Next */}
        {media.length > 1 && (
          <button
            onClick={next}
            className="absolute right-2 z-10 flex items-center justify-center w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 text-white transition-colors"
            aria-label="Next"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        )}
      </div>

      {/* Dots */}
      {media.length > 1 && (
        <div className="flex justify-center gap-1.5 pb-4">
          {media.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrentIndex(i)}
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentIndex ? "bg-primary" : "bg-gray-300"
              }`}
              aria-label={`Go to image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </Modal>
  );
};
